import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useSettings } from '../../contexts/SettingsContext';
import { queueNotification, shouldShowNotification } from '../../utils/notificationManager';
import { 
  useSuppliersData, 
  usePackagesData, 
  useFinancialData, 
  useReservationsData,
  useClientsData,
  useDashboardData,
  deleteSupplier 
} from '../../services/dataService';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Menu,
  MenuList,
  MenuItem as MenuListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Rating,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText as MuiListItemText,
  ListItemAvatar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Badge,
  Skeleton,
  Grow,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Business, FilterAlt as FilterAltIcon } from '@mui/icons-material';
import FilterBar from '../../components/common/FilterBar';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  MoreVert,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  DirectionsBus as BusIcon,
  LocalTaxi as TaxiIcon,
  Restaurant as RestaurantIcon,
  Security as InsuranceIcon,
  Tour as TourIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  GetApp as ExportIcon,
  FilterList as FilterIcon,
  Handshake as HandshakeIcon,
  Assessment as AssessmentIcon,
  ViewList as ViewListIcon,
  ViewList,
  ViewModule as ViewModuleIcon,
  ViewList as TableViewIcon,
  ViewModule as CardViewIcon,
  Print as PrintIcon,
  PersonAdd as PersonAddIcon,
  Assignment as AssignmentIcon,
  Insights as InsightsIcon,
  CardTravel as CardTravelIcon,
  AccountBalance as AccountBalanceIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
  Public as PublicIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PictureAsPdfIcon,
  BarChart,
  ArrowForward,
  Today
} from '@mui/icons-material';

import SupplierDetails from '../../components/suppliers/SupplierDetails';

// FloatingActionCard component from Dashboard
const FloatingActionCard = ({ title, icon, color, action, description, badge, onClick }) => {
  // Get theme and check if mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Card sx={{
    background: `linear-gradient(135deg, ${color}15, white)`,
      border: `1px solid ${color}30`,
      borderRadius: { xs: 3, md: 4 },
      overflow: 'hidden',
      position: 'relative',
      cursor: 'pointer',
      height: '100%', // Ensure consistent height
    }} onClick={onClick}>
      <CardContent sx={{ 
        p: { xs: 2, sm: 2.5, md: 3 }, 
        position: 'relative', 
        zIndex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 1, md: 2 }, 
          mb: { xs: 1.5, md: 2 },
          flexWrap: 'wrap'
        }}>
          <Avatar className="action-icon" sx={{ 
            background: `linear-gradient(135deg, ${color}, ${color}cc)`,
            width: { xs: 48, sm: 52, md: 56 },
            height: { xs: 48, sm: 52, md: 56 },
            boxShadow: `0 8px 25px ${color}40`,
            '& .MuiSvgIcon-root': {
              fontSize: { xs: 26, sm: 28, md: 30 }
            }
          }}>
            {icon}
          </Avatar>
          {badge && (
            <Chip 
              label={badge} 
              size="small" 
              sx={{ 
                background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                color: 'white',
                fontWeight: 600,
                height: { xs: '20px', md: '24px' },
                fontSize: { xs: '0.7rem', md: '0.75rem' }
              }}
            />
          )}
        </Box>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700, 
            mb: { xs: 0.5, md: 1 }, 
            color: 'rgba(51,65,85,0.9)',
            lineHeight: 1.3,
            fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' }
          }}
        >
          {title}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'rgba(51,65,85,0.7)',
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            display: { xs: '-webkit-box', md: 'block' },
            WebkitLineClamp: { xs: 2, md: 'none' },
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

// EnhancedStatCard from Dashboard
const EnhancedStatCard = ({ title, value, icon, gradient, delay, onClick, subtitle, chart, trend, progress }) => (
  <Grow in={true} style={{ transformOrigin: '0 0 0' }} timeout={1000}>
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        background: gradient ? `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` : 'white',
        color: gradient ? 'white' : 'inherit',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'transform 0.3s, box-shadow 0.3s',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          transform: 'translateY(-5px)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
        } : {},
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ 
              opacity: gradient ? 0.9 : 0.7, 
              mb: 0.5, 
              fontWeight: 500,
              fontSize: { xs: '0.8rem', md: '0.875rem' }
            }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ 
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.75rem' }
            }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ 
                opacity: gradient ? 0.9 : 0.7, 
                mt: 0.5,
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              }}>
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Chip 
                label={trend} 
                size="small" 
                sx={{ 
                  mt: 1,
                  backgroundColor: trend.startsWith('+') ? 
                    (gradient ? 'rgba(255,255,255,0.3)' : '#059669') : 
                    (gradient ? 'rgba(255,255,255,0.3)' : '#dc2626'),
                  color: gradient ? 'white' : 'white',
                  fontWeight: 600,
                  fontSize: { xs: '0.65rem', md: '0.75rem' },
                  height: { xs: 20, md: 24 }
                }}
              />
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: gradient ? 'rgba(255,255,255,0.2)' : 'primary.light',
              color: gradient ? 'white' : 'primary.main',
              width: { xs: 40, md: 48 },
              height: { xs: 40, md: 48 }
            }}
          >
            {icon}
          </Avatar>
        </Box>
        
        {progress && (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7, color: 'white' }}>
                Progression
              </Typography>
              <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7, color: 'white' }}>
                {progress}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: gradient ? 'rgba(255,255,255,0.2)' : 'rgba(99,102,241,0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: gradient ? 'white' : 'primary.main',
                }
              }} 
            />
          </Box>
        )}
        
        {chart && (
          <Box sx={{ mt: 2, height: { xs: 40, md: 60 } }}>
            {chart}
          </Box>
        )}
      </CardContent>
    </Card>
  </Grow>
);

// Enhanced charts components
const SupplierPerformanceChart = ({ data }) => (
  <Box sx={{ height: 200, bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
      Performance des Fournisseurs (Score sur 100)
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'end', height: '70%', gap: 1 }}>
      {data.map((supplier, index) => (
        <Tooltip key={index} title={`${supplier.name}: ${supplier.score}%`}>
          <Box
            sx={{
              flex: 1,
              height: `${supplier.score}%`,
              bgcolor: supplier.score > 80 ? '#4caf50' : supplier.score > 60 ? '#ff9800' : '#f44336',
              borderRadius: '4px 4px 0 0',
              minHeight: '10px',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': { opacity: 0.8, transform: 'scaleY(1.05)' }
            }}
          />
        </Tooltip>
      ))}
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
      {data.map((supplier, index) => (
        <Typography key={index} variant="caption" color="text.secondary" sx={{ transform: 'rotate(-45deg)', fontSize: '0.65rem' }}>
          {supplier.name}
        </Typography>
      ))}
    </Box>
  </Box>
);

const SupplierDistributionChart = ({ data }) => (
  <Box sx={{ height: 200, bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
      Distribution par Type de Service
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60%' }}>
      <Box sx={{ position: 'relative', width: 120, height: 120 }}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: `conic-gradient(
              #1976d2 0deg ${data.compagnies * 3.6}deg,
              #2e7d32 ${data.compagnies * 3.6}deg ${(data.compagnies + data.hotels) * 3.6}deg,
              #ed6c02 ${(data.compagnies + data.hotels) * 3.6}deg ${(data.compagnies + data.hotels + data.transport) * 3.6}deg,
              #9c27b0 ${(data.compagnies + data.hotels + data.transport) * 3.6}deg 360deg
            )`
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '60%',
            height: '60%',
            bgcolor: 'white',
            borderRadius: '50%'
          }}
        />
      </Box>
    </Box>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1 }}>
      <Chip label={`Aérien ${data.compagnies}%`} size="small" sx={{ bgcolor: '#1976d2', color: 'white' }} />
      <Chip label={`Hôtels ${data.hotels}%`} size="small" sx={{ bgcolor: '#2e7d32', color: 'white' }} />
      <Chip label={`Transport ${data.transport}%`} size="small" sx={{ bgcolor: '#ed6c02', color: 'white' }} />
      <Chip label={`Autres ${data.autres}%`} size="small" sx={{ bgcolor: '#9c27b0', color: 'white' }} />
    </Box>
  </Box>
);

const Fournisseurs = () => {
  // Settings context for real-time data
  const { formatCurrency, settings, formatDate } = useSettings();
  
  // Use data services for real-time interconnected data - with optimized loading
  // Only load suppliers data initially, other data will be loaded as needed
  const { suppliers, loading: suppliersLoading, error: suppliersError, stats: suppliersStats } = useSuppliersData();
  
  // Lazy load other data with skipFinancial=true to prevent circular dependencies
  const { packages, loading: packagesLoading } = usePackagesData({ skipFinancial: true });
  const { financialData: transactions, loading: transactionsLoading } = useFinancialData({ skipSuppliers: true });
  const { reservations, loading: reservationsLoading } = useReservationsData();
  const { clients, loading: clientsLoading } = useClientsData();
  
  // Don't load dashboard data unless needed
  const [dashboardLoading, setDashboardLoading] = useState(false);
  
  // Use real loading state from suppliers data
  const isLoading = suppliersLoading;

  // All useMemo hooks need to be at the top level, before any conditional logic
  // Get interconnected packages with supplier data
  const interconnectedPackages = useMemo(() => {
    const result = [];
    if (packages && suppliers && packages.length > 0 && suppliers.length > 0) {
      return packages.filter(pkg => {
        // Check if package has a supplier reference
        return pkg.fournisseurId && suppliers.some(s => s.id === pkg.fournisseurId);
      });
    }
    return result;
  }, [packages, suppliers]);
  
  // Get interconnected transactions with supplier data - with lazy loading
  const interconnectedTransactions = useMemo(() => {
    // Only calculate this if transactions are actually needed and loaded
    if (!transactions) return [];
    
    // If transactions are loaded, filter them
    if (transactions && suppliers && transactions.length > 0 && suppliers.length > 0) {
      return transactions.filter(transaction => {
        // Check if transaction is an expense and has a supplier reference
        return transaction.type === 'Dépense' && 
               transaction.categorie === 'Fournisseur' && 
               transaction.fournisseurId && 
               suppliers.some(s => s.id === transaction.fournisseurId);
      });
    }
    return [];
  }, [transactions, suppliers]);
  
  // Get reservations connected to suppliers through packages - with lazy loading
  const interconnectedReservations = useMemo(() => {
    // Only calculate if reservations and packages are actually loaded
    if (!reservations || !packages) return [];
    
    // If data is loaded, filter reservations
    if (reservations && packages && suppliers) {
      // Use a more efficient approach to avoid excessive filtering
      // First create a set of supplier IDs for faster lookups
      const supplierIds = new Set(suppliers.map(s => s.id));
      
      // Then create a map of packages with their supplier IDs for faster lookups
      const packageSupplierMap = {};
      packages.forEach(p => {
        if (p.fournisseurId) {
          packageSupplierMap[p.id] = p.fournisseurId;
        }
      });
      
      // Now filter reservations
      return reservations.filter(reservation => {
        const supplierId = packageSupplierMap[reservation.forfaitId];
        return supplierId && supplierIds.has(supplierId);
      });
    }
    return [];
  }, [reservations, packages, suppliers]);
  
  // Calculate supplier performance metrics
  const supplierMetrics = useMemo(() => {
    if (!suppliers || !transactions || !reservations || !packages) {
      return { topPerformers: [], lowPerformers: [], revenueBySupplier: {} };
    }
    
    // Calculate revenue by supplier
    const revenueBySupplier = suppliers.reduce((acc, supplier) => {
      // Find all transactions related to this supplier
      const supplierTransactions = transactions.filter(t => 
        t.fournisseurId === supplier.id
      );
      
      // Calculate total revenue
      const totalRevenue = supplierTransactions.reduce((sum, t) => 
        sum + (t.montant || 0), 0
      );
      
      acc[supplier.id] = totalRevenue;
      return acc;
    }, {});
    
    // Sort suppliers by performance score
    const sortedSuppliers = [...(suppliers || [])].sort((a, b) => 
      ((b.performanceScore || 0) - (a.performanceScore || 0))
    );
    
    return {
      topPerformers: sortedSuppliers.slice(0, 3),
      lowPerformers: sortedSuppliers.slice(-3).reverse(),
      revenueBySupplier
    };
  }, [suppliers, transactions, reservations, packages]);
  
  // Calculate type distribution based on actual supplier data
  const typeDistribution = useMemo(() => {
    const defaultDistribution = { compagnies: 25, hotels: 25, transport: 25, autres: 25 };
    
    if (!suppliers || suppliers.length === 0) {
      return defaultDistribution;
    }
    
    // Count suppliers by type
    const typeCounts = suppliers.reduce((acc, supplier) => {
      const type = supplier.type?.toLowerCase() || 'autres';
      
      if (type.includes('aérien') || type.includes('compagnie')) {
        acc.compagnies = (acc.compagnies || 0) + 1;
      } else if (type.includes('hôtel') || type.includes('hotel') || type.includes('hébergement')) {
        acc.hotels = (acc.hotels || 0) + 1;
      } else if (type.includes('transport') || type.includes('taxi') || type.includes('bus')) {
        acc.transport = (acc.transport || 0) + 1;
      } else {
        acc.autres = (acc.autres || 0) + 1;
      }
      
      return acc;
    }, { compagnies: 0, hotels: 0, transport: 0, autres: 0 });
    
    // Calculate percentages
    const total = Object.values(typeCounts).reduce((sum, count) => sum + count, 0) || 1;
    
    return {
      compagnies: Math.round((typeCounts.compagnies / total) * 100),
      hotels: Math.round((typeCounts.hotels / total) * 100),
      transport: Math.round((typeCounts.transport / total) * 100),
      autres: Math.round((typeCounts.autres / total) * 100)
    };
  }, [suppliers]);
  
  // Calculate total revenue from transactions related to suppliers
  const totalChiffreAffaires = useMemo(() => {
    let total = 0;
    
    if (transactions && transactions.length > 0) {
      total = transactions
        .filter(t => t.type === 'Dépense' && t.categorie === 'Fournisseur')
        .reduce((sum, t) => sum + (t.montant || 0), 0);
    }
    
    return total;
  }, [transactions]);
  
  // Calculate average performance score
  const performanceMoyenne = useMemo(() => {
    let average = 0;
    
    if (suppliers && suppliers.length > 0) {
      const totalScore = suppliers.reduce((sum, s) => sum + (s.performanceScore || 0), 0);
      average = totalScore / suppliers.length;
    }
    
    return average;
  }, [suppliers]);
  
  // Calculate average rating
  const noteMoyenne = useMemo(() => {
    let average = 0;
    
    if (suppliers && suppliers.length > 0) {
      const totalRating = suppliers.reduce((sum, s) => sum + (s.rating || 0), 0);
      average = totalRating / suppliers.length;
    }
    
    return average;
  }, [suppliers]);
  
  // Count unique clients who have booked with these suppliers
  const totalClients = useMemo(() => {
    let count = 0;
    
    if (reservations && packages && suppliers) {
      // Get reservations connected to suppliers
      const supplierReservations = reservations.filter(res => {
        const pkg = packages.find(p => p.id === res.forfaitId);
        return pkg && pkg.fournisseurId && suppliers.some(s => s.id === pkg.fournisseurId);
      });
      
      // Count unique clients
      const uniqueClientIds = new Set(supplierReservations.map(res => res.clientId));
      count = uniqueClientIds.size;
    }
    
    return count;
  }, [reservations, packages, suppliers]);
  
  // Get unique countries and types for filters
  const uniqueCountries = useMemo(() => {
    if (!suppliers) return [];
    return [...new Set(suppliers.map(f => f.pays).filter(Boolean) || [])];
  }, [suppliers]);
  
  const uniqueTypes = useMemo(() => {
    if (!suppliers) return [];
    return [...new Set(suppliers.map(f => f.type).filter(Boolean) || [])];
  }, [suppliers]);
  
  // Chart data from real-time suppliers
  const performanceChartData = useMemo(() => {
    if (!suppliers || suppliers.length === 0) {
      return [];
    }
    
    return suppliers.slice(0, 6).map(f => ({
      name: f.nom?.substring(0, 8) || '',
      score: f.performanceScore || 0 // Use 0 if no score available
    }));
  }, [suppliers]);
  
  // Enhanced statistics with interconnected data from real-time services
  const totalFournisseurs = suppliers?.length || 0;
  const fournisseursActifs = suppliers?.filter(s => s.statut === 'Actif')?.length || 0;
  
  const statsCards = [
    {
      title: 'Total Fournisseurs',
      value: totalFournisseurs,
      icon: BusinessIcon,
      color: 'primary',
      trend: '+12%',
      subtitle: `${fournisseursActifs} actifs`
    },
    {
      title: 'Chiffre d\'Affaires',
      value: totalChiffreAffaires,
      icon: MoneyIcon,
      color: 'success',
      trend: '+25%',
      isCurrency: true,
      subtitle: 'Revenue total généré'
    },
    {
      title: 'Performance Moyenne',
      value: `${performanceMoyenne.toFixed(1)}%`,
      icon: SpeedIcon,
      color: 'info',
      trend: '+3.2%',
      subtitle: 'Score de performance'
    },
    {
      title: 'Satisfaction Client',
      value: noteMoyenne.toFixed(1),
      icon: StarIcon,
      color: 'warning',
      trend: '+0.3',
      subtitle: `Sur ${totalClients} clients`
    }
  ];
  
  const [filteredFournisseurs, setFilteredFournisseurs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [viewMode, setViewMode] = useState('table');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFournisseur, setEditingFournisseur] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFournisseur, setSelectedFournisseur] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [formData, setFormData] = useState({
    nom: '',
    type: 'Hôtel',
    pays: '',
    ville: '',
    email: '',
    telephone: '',
    adresse: '',
    personneContact: '',
    statut: 'Actif',
    commission: 0,
    delaiPaiement: 30,
    description: ''
  });

  const [activeTab, setActiveTab] = useState(0);
  const showQuickActions = false;

  // DEFINE handleOpenDetails EARLY using useCallback to avoid reference errors
  const handleOpenDetails = useCallback((fournisseur) => {
    if (!fournisseur) return;
    
    try {
      // Create a clean supplier object with only the data that exists
      // and was entered during creation
      const enhancedSupplier = { ...fournisseur };
      
     // Use current hook data
     const latestPackages = packages || [];
     const latestReservations = reservations || [];
     const latestTransactions = transactions || [];
     const latestClients = clients || [];
      
      // Find related packages if they exist
      if (latestPackages && latestPackages.length > 0) {
        const relatedPackages = latestPackages.filter(pkg => pkg.fournisseurId === fournisseur.id);
        if (relatedPackages.length > 0) {
          enhancedSupplier.forfaitsGeres = relatedPackages.map(pkg => ({
            id: pkg.id,
            nom: pkg.nom,
            prix: pkg.prix,
            categorie: pkg.categorie,
            destination: pkg.destination,
            statut: pkg.statut
          }));
          
          // Find reservations related to these packages
          if (latestReservations && latestReservations.length > 0) {
            const packageIds = relatedPackages.map(pkg => pkg.id);
            const relatedReservations = latestReservations.filter(res => 
              packageIds.includes(res.forfaitId)
            );
            
            // Add reservation count to each package
            enhancedSupplier.forfaitsGeres = enhancedSupplier.forfaitsGeres.map(pkg => {
              const reservationCount = relatedReservations.filter(res => res.forfaitId === pkg.id).length;
              return {
                ...pkg,
                reservationCount
              };
            });
          }
        }
      }
      
      // Set the supplier object without adding any auto-generated data
      setSelectedFournisseur(enhancedSupplier);
      setDetailsOpen(true);
      setAnchorEl(null);
    } catch (error) {
      console.error('Error preparing supplier details:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des détails du fournisseur',
        severity: 'error'
      });
    }
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailsOpen(false);
  }, []);

  // Filter suppliers based on search term and filters
  useEffect(() => {
    console.log('Suppliers data:', { suppliersLoading, suppliers: suppliers?.length || 0, error: suppliersError });
    
    // Use real suppliers from API - no more mock/sample data
    let suppliersToUse = suppliers || [];
    if (!suppliersLoading && suppliersToUse.length === 0) {
      console.log('No suppliers found - database is clean');
    }
    
    if (!suppliersLoading && suppliersToUse) {
      console.log('Filtering suppliers:', suppliersToUse.length, suppliersToUse);
      let filtered = [...suppliersToUse];
      
      // Apply search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(f => 
          (f.nom && f.nom.toLowerCase().includes(search)) || 
          (f.type && f.type.toLowerCase().includes(search)) ||
          (f.pays && f.pays.toLowerCase().includes(search)) ||
          (f.ville && f.ville.toLowerCase().includes(search)) ||
          (f.personneContact && f.personneContact.toLowerCase().includes(search))
        );
      }
      
      // Apply type filter
      if (filterType !== 'all') {
        filtered = filtered.filter(f => f.type === filterType);
      }
      
      // Apply status filter
      if (filterStatus !== 'all') {
        filtered = filtered.filter(f => f.statut === filterStatus);
      }
      
      // Apply location filter
      if (filterLocation !== 'all') {
        filtered = filtered.filter(f => f.pays === filterLocation);
      }
    
      // CRITICAL: Only update filtered suppliers if we're not in the middle of an edit
      // Check if we have unsaved changes that might be lost
      const isEditing = openDialog && editingFournisseur;
      
      if (!isEditing) {
      setFilteredFournisseurs(filtered);
      } else {
        console.log('Skipping filter update while editing to prevent data loss');
      }
    }
  }, [
    suppliers, filterType, filterStatus, filterLocation, searchTerm, 
    suppliersLoading, openDialog, editingFournisseur
  ]);

  // Function to update suppliers data
  const handleSupplierDataUpdate = () => {
    try {
     const updatedSuppliers = suppliers || [];
      
      // First update the filtered suppliers list directly to ensure UI consistency
      if (Array.isArray(updatedSuppliers)) {
        console.log('Updating suppliers data with latest from localStorage');
        
        // Apply current filters to the updated suppliers
        let filtered = [...updatedSuppliers];
        
        // Apply search filter
        if (searchTerm) {
          const search = searchTerm.toLowerCase();
          filtered = filtered.filter(f => 
            (f.nom && f.nom.toLowerCase().includes(search)) || 
            (f.type && f.type.toLowerCase().includes(search)) ||
            (f.pays && f.pays.toLowerCase().includes(search)) ||
            (f.ville && f.ville.toLowerCase().includes(search)) ||
            (f.personneContact && f.personneContact.toLowerCase().includes(search))
          );
        }
        
        // Apply type filter
        if (filterType !== 'all') {
          filtered = filtered.filter(f => f.type === filterType);
        }
        
        // Apply status filter
        if (filterStatus !== 'all') {
          filtered = filtered.filter(f => f.statut === filterStatus);
        }
        
        // Apply location filter
        if (filterLocation !== 'all') {
          filtered = filtered.filter(f => f.pays === filterLocation);
        }
      
        // Update the state directly
        setFilteredFournisseurs(filtered);
      }
      
      // Show notification
      setSnackbar({
        open: true,
        message: 'Données des fournisseurs mises à jour en temps réel',
        severity: 'info'
      });
    } catch (error) {
      console.error('Error updating suppliers data:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la mise à jour des données',
        severity: 'error'
      });
    }
  };

  // Function to force refresh all data
  const refreshAllData = useCallback(() => {
    // Clear cache to force reload
    // No localStorage cache to clear
    
    try {
    const updatedSuppliers = suppliers || [];
      
      // Apply current filters to get filtered suppliers
      let filtered = [...updatedSuppliers];
      
      // Apply search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(f => 
          (f.nom && f.nom.toLowerCase().includes(search)) || 
          (f.type && f.type.toLowerCase().includes(search)) ||
          (f.pays && f.pays.toLowerCase().includes(search)) ||
          (f.ville && f.ville.toLowerCase().includes(search)) ||
          (f.personneContact && f.personneContact.toLowerCase().includes(search))
        );
      }
      
      // Apply type filter
      if (filterType !== 'all') {
        filtered = filtered.filter(f => f.type === filterType);
      }
      
      // Apply status filter
      if (filterStatus !== 'all') {
        filtered = filtered.filter(f => f.statut === filterStatus);
      }
      
      // Apply location filter
      if (filterLocation !== 'all') {
        filtered = filtered.filter(f => f.pays === filterLocation);
      }
      
      // Update the state directly to ensure immediate UI update
      setFilteredFournisseurs(filtered);
    
    // Dispatch events to notify other components
    window.dispatchEvent(new CustomEvent('supplierDataUpdate'));
    window.dispatchEvent(new CustomEvent('packageDataUpdate'));
    window.dispatchEvent(new CustomEvent('financialDataUpdate'));
    window.dispatchEvent(new CustomEvent('reservationDataUpdate'));
    window.dispatchEvent(new CustomEvent('clientDataUpdate'));
    
    setSnackbar({
      open: true,
      message: 'Toutes les données ont été actualisées',
      severity: 'success'
    });
    } catch (error) {
      console.error('Error refreshing data:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'actualisation des données',
        severity: 'error'
      });
    }
  }, [searchTerm, filterType, filterStatus, filterLocation]);

  // Add event listeners for real-time data updates from other pages
  useEffect(() => {
  // Remove localStorage storage events
    const handleStorageChange = (e) => {
      if (e.key === 'suppliersData') {
        // Don't rely on complex event handling - use a direct approach
        try {
        const updatedSuppliers = suppliers || [];
        
          // Update our global reference
          if (typeof window.__travelIT === 'undefined') {
            window.__travelIT = {};
          }
          window.__travelIT.suppliers = updatedSuppliers;
          
          // Instead of complex filtering, just force a re-render with new data
          // When we force the update, we must also apply the current filters
          let filtered = [...updatedSuppliers];
          
          // Apply all filters again
          if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(f => 
              (f.nom && f.nom.toLowerCase().includes(search)) || 
              (f.type && f.type.toLowerCase().includes(search)) ||
              (f.pays && f.pays.toLowerCase().includes(search)) ||
              (f.ville && f.ville.toLowerCase().includes(search)) ||
              (f.personneContact && f.personneContact.toLowerCase().includes(search))
            );
          }
          
          if (filterType !== 'all') {
            filtered = filtered.filter(f => f.type === filterType);
          }
          
          if (filterStatus !== 'all') {
            filtered = filtered.filter(f => f.statut === filterStatus);
          }
          
          if (filterLocation !== 'all') {
            filtered = filtered.filter(f => f.pays === filterLocation);
          }
          
          // Update the filtered list directly
          setFilteredFournisseurs(filtered);
          
          // Completely disable notifications for storage events
          // Users don't need to know about every data sync
          window.__travelIT.suppressNextNotification = false;
        } catch (error) {
          console.error('Error processing supplier data update:', error);
        }
      }
    };

    // Function to handle custom events from other components
    const handleSupplierEventUpdate = (e) => {
      // Set silent mode for data sync events
      if (typeof window.__travelIT === 'undefined') {
        window.__travelIT = {};
      }
      window.__travelIT.silentMode = true;
      
      // Check if we have a complete payload with all suppliers
      if (e.detail && e.detail.allSuppliers && Array.isArray(e.detail.allSuppliers)) {
        // Use the full suppliers list provided in the event
        const updatedSuppliers = e.detail.allSuppliers;
        
        // Apply all filters to get the filtered list
        let filtered = [...updatedSuppliers];
        
        // Apply search filter
        if (searchTerm) {
          const search = searchTerm.toLowerCase();
          filtered = filtered.filter(f => 
            (f.nom && f.nom.toLowerCase().includes(search)) || 
            (f.type && f.type.toLowerCase().includes(search)) ||
            (f.pays && f.pays.toLowerCase().includes(search)) ||
            (f.ville && f.ville.toLowerCase().includes(search)) ||
            (f.personneContact && f.personneContact.toLowerCase().includes(search))
          );
        }
        
        // Apply type filter
        if (filterType !== 'all') {
          filtered = filtered.filter(f => f.type === filterType);
        }
        
        // Apply status filter
        if (filterStatus !== 'all') {
          filtered = filtered.filter(f => f.statut === filterStatus);
        }
        
        // Apply location filter
        if (filterLocation !== 'all') {
          filtered = filtered.filter(f => f.pays === filterLocation);
        }
        
        // Update the filtered list directly
        setFilteredFournisseurs(filtered);
      }
      // If we have just a single supplier update
      else if (e.detail && e.detail.action && e.detail.supplier) {
        const { action, supplier } = e.detail;
        
        if (action === 'update') {
          // Update the supplier in the filtered list
          setFilteredFournisseurs(prevFiltered => {
            return prevFiltered.map(item => 
              item.id === supplier.id ? supplier : item
            );
          });
        }
        else if (action === 'create') {
          // Check if the new supplier matches our filters
          const shouldAdd = 
            (filterType === 'all' || supplier.type === filterType) &&
            (filterStatus === 'all' || supplier.statut === filterStatus) &&
            (filterLocation === 'all' || supplier.pays === filterLocation);
          
          if (shouldAdd) {
            setFilteredFournisseurs(prev => [...prev, supplier]);
          }
        }
        else if (action === 'delete' && e.detail.supplierId) {
          // Remove the supplier from the filtered list
          setFilteredFournisseurs(prevFiltered => 
            prevFiltered.filter(s => s.id !== e.detail.supplierId)
          );
        }
      }
      
      // If a supplier details dialog is open, refresh its data
      if (detailsOpen && selectedFournisseur) {
        try {
        const updatedSuppliers = suppliers || [];
        const updatedSupplier = updatedSuppliers.find(s => s.id === selectedFournisseur.id);
        if (updatedSupplier) {
          handleOpenDetails(updatedSupplier);
          }
        } catch (error) {
          console.error('Error updating supplier details:', error);
        }
      }
    };

    // Other event handlers (keep existing ones)
    const handlePackageDataUpdate = (e) => {
      // Use notification manager to batch notifications
      if (shouldShowNotification()) {
        queueNotification({
          message: 'Forfaits mis à jour',
          severity: 'info'
        }, setSnackbar);
      }
      
      // If a supplier details dialog is open, refresh its data
      if (detailsOpen && selectedFournisseur) {
        handleOpenDetails(selectedFournisseur);
      }
    };

    const handleFinancialDataUpdate = (e) => {
      // Use notification manager to batch notifications
      if (shouldShowNotification()) {
        queueNotification({
          message: 'Données financières mises à jour',
          severity: 'info'
        }, setSnackbar);
      }
      
      // If a supplier details dialog is open, refresh its data
      if (detailsOpen && selectedFournisseur) {
        handleOpenDetails(selectedFournisseur);
      }
    };
    
    const handleReservationDataUpdate = (e) => {
      // Use notification manager to batch notifications
      if (shouldShowNotification()) {
        queueNotification({
          message: 'Réservations mises à jour',
          severity: 'info'
        }, setSnackbar);
      }
      
      // If a supplier details dialog is open, refresh its data
      if (detailsOpen && selectedFournisseur) {
        handleOpenDetails(selectedFournisseur);
      }
    };
    
    const handleClientDataUpdate = (e) => {
      // Use notification manager to batch notifications
      if (shouldShowNotification()) {
        queueNotification({
          message: 'Clients mis à jour',
          severity: 'info'
        }, setSnackbar);
      }
      
      // If a supplier details dialog is open, refresh its data
      if (detailsOpen && selectedFournisseur) {
        handleOpenDetails(selectedFournisseur);
      }
    };

    // Add event listeners
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('supplierDataUpdate', handleSupplierEventUpdate);
    window.addEventListener('packageDataUpdate', handlePackageDataUpdate);
    window.addEventListener('financialDataUpdate', handleFinancialDataUpdate);
    window.addEventListener('reservationDataUpdate', handleReservationDataUpdate);
    window.addEventListener('clientDataUpdate', handleClientDataUpdate);

    // Clean up event listeners
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('supplierDataUpdate', handleSupplierEventUpdate);
      window.removeEventListener('packageDataUpdate', handlePackageDataUpdate);
      window.removeEventListener('financialDataUpdate', handleFinancialDataUpdate);
      window.removeEventListener('reservationDataUpdate', handleReservationDataUpdate);
      window.removeEventListener('clientDataUpdate', handleClientDataUpdate);
    };
  }, [
    filterType, filterStatus, filterLocation, searchTerm, 
    suppliersLoading, detailsOpen, selectedFournisseur,
    handleOpenDetails // Add this dependency since we're using it
  ]);

  // Render loading state
  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' } }}>
          Gestion des Fournisseurs
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' } }}>
          Chargement des données des fournisseurs...
        </Typography>
        
        <LinearProgress sx={{ mb: 3 }} />
        
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
        
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 3 }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 3 }} />
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 3 }}>
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
        </Box>
      </Box>
    );
  }
  
  // Render error state
  if (suppliersError) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' } }}>
          Gestion des Fournisseurs
        </Typography>
        <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
          Erreur lors du chargement des données des fournisseurs: {suppliersError}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
            color: 'white',
            px: 4, 
            py: 1,
            '&:hover': {
              background: 'linear-gradient(135deg, #1e3a8a 0%, #6d28d9 100%)'
            }
          }}
        >
          Réessayer
        </Button>
      </Box>
    );
  }

  const handleOpenDialog = (fournisseur = null) => {
    if (fournisseur) {
      setEditingFournisseur(fournisseur);
      setFormData({
        nom: fournisseur.nom,
        type: fournisseur.type,
        pays: fournisseur.pays,
        ville: fournisseur.ville,
        email: fournisseur.email,
        telephone: fournisseur.telephone,
        adresse: fournisseur.adresse,
        personneContact: fournisseur.personneContact,
        statut: fournisseur.statut,
        commission: fournisseur.commission,
        delaiPaiement: fournisseur.delaiPaiement,
        description: fournisseur.description
      });
    } else {
      setEditingFournisseur(null);
      setFormData({
        nom: '',
        type: 'Hôtel',
        pays: '',
        ville: '',
        email: '',
        telephone: '',
        adresse: '',
        personneContact: '',
        statut: 'Actif',
        commission: 0,
        delaiPaiement: 30,
        description: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFournisseur(null);
  };

  const handleSaveFournisseur = () => {
    try {
  const existingSuppliers = suppliers || [];
      
      let updatedSuppliers;
      let updatedFournisseur;
      
      if (editingFournisseur) {
        // Update existing supplier
        updatedFournisseur = { 
          ...editingFournisseur, 
          ...formData,
          performanceScore: formData.performanceScore || editingFournisseur.performanceScore || 0,
          satisfactionClient: typeof formData.satisfactionClient === 'number' 
            ? formData.satisfactionClient 
            : typeof editingFournisseur.satisfactionClient === 'number'
              ? editingFournisseur.satisfactionClient
              : 0,
          lastUpdated: new Date().toISOString()
        };
        
        updatedSuppliers = existingSuppliers.map(supplier => 
          supplier.id === editingFournisseur.id ? updatedFournisseur : supplier
        );
      } else {
        // Create new supplier with unique ID
        const newId = `supplier_${Date.now()}`;
        updatedFournisseur = {
          id: newId,
          ...formData,
          performanceScore: 0,
          satisfactionClient: 0,
          nombreClients: 0,
          nombreServices: 0,
          chiffreAffaires: 0,
          delaiMoyenLivraison: 0,
          dateCreation: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        };
        
        updatedSuppliers = [...existingSuppliers, updatedFournisseur];
      }
      
      // CRITICAL FIX: First close dialog before any state updates that could trigger re-renders
      handleCloseDialog();

      // Set a flag to prevent notification cascades
      if (typeof window.__travelIT === 'undefined') {
        window.__travelIT = {};
      }
      window.__travelIT.suppressNextNotification = true;
      window.__travelIT.suppliers = updatedSuppliers;
      window.__travelIT.updatedFournisseur = updatedFournisseur;
      
  // Notify listeners
      
      // CRITICAL FIX: Update the current component state directly
      // This ensures immediate UI updates without waiting for event propagation
      setFilteredFournisseurs(prevFiltered => {
        if (editingFournisseur) {
          // Replace the updated supplier in the array
          return prevFiltered.map(supplier => 
            supplier.id === updatedFournisseur.id ? updatedFournisseur : supplier
          );
        } else {
          // Check if the new supplier matches current filters
          const shouldAdd = 
            (filterType === 'all' || updatedFournisseur.type === filterType) &&
            (filterStatus === 'all' || updatedFournisseur.statut === filterStatus) &&
            (filterLocation === 'all' || updatedFournisseur.pays === filterLocation);
          
          return shouldAdd ? [...prevFiltered, updatedFournisseur] : prevFiltered;
        }
      });
      
      // Show success message
      setSnackbar({
        open: true,
        message: editingFournisseur 
          ? 'Fournisseur modifié avec succès' 
          : 'Fournisseur ajouté avec succès',
        severity: 'success'
      });
      
      // Dispatch a single event with complete supplier data
      // Use a timeout to ensure the localStorage update is complete
      setTimeout(() => {
        const event = new CustomEvent('supplierDataUpdate', { 
          detail: { 
            action: editingFournisseur ? 'update' : 'create',
            supplier: updatedFournisseur,
            allSuppliers: updatedSuppliers
          } 
        });
        window.dispatchEvent(event);
      }, 100);
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du fournisseur:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la sauvegarde du fournisseur',
        severity: 'error'
      });
    }
  };

  const handleDeleteFournisseur = async (fournisseurId) => {
    try {
      if (!fournisseurId) return;

      // Clear UI state first for immediate feedback
      setAnchorEl(null);
      setSelectedFournisseur(null);

      // Update UI immediately before API call to ensure responsiveness
      setFilteredFournisseurs(prev => prev.filter(s => s.id !== fournisseurId));
      
      console.log(`🗑️ Deleting supplier ${fournisseurId} - UI updated first`);
      
      // Appel API – suppression sur le backend
      await deleteSupplier(fournisseurId);

      // Force cache invalidation regardless of API response
      window.dispatchEvent(new CustomEvent('suppliersDataUpdate'));

      setSnackbar({
        open: true,
        message: 'Fournisseur supprimé avec succès',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erreur lors de la suppression du fournisseur:', error);
      
      // Even on error, keep the supplier removed from UI
      // This ensures consistent behavior even with backend issues
      
      setSnackbar({
        open: true,
        message: `Le fournisseur a été supprimé de l'interface, mais une erreur est survenue: ${error.message}`,
        severity: 'warning'
      });
    }
  };

  const handleMenuClick = (event, fournisseur) => {
    setAnchorEl(event.currentTarget);
    setSelectedFournisseur(fournisseur);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFournisseur(null);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Compagnie aérienne': return <FlightIcon />;
      case 'Hôtel': return <HotelIcon />;
      case 'Transport': return <BusIcon />;
      case 'Restaurant': return <RestaurantIcon />;
      case 'Assurance': return <InsuranceIcon />;
      case 'Tour opérateur': return <TourIcon />;
      default: return <BusinessIcon />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Compagnie aérienne': return 'primary';
      case 'Hôtel': return 'success';
      case 'Transport': return 'info';
      case 'Restaurant': return 'warning';
      case 'Assurance': return 'error';
      case 'Tour opérateur': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'Actif': return 'success';
      case 'En pause': return 'warning';
      case 'Inactif': return 'error';
      default: return 'default';
    }
  };

  // Pagination for card view
  const paginatedFournisseurs = filteredFournisseurs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Handler functions for supplier actions
  const handleViewFournisseur = (fournisseur) => {
    setSelectedFournisseur(fournisseur);
    setDetailsOpen(true);
  };

  const handleEditFournisseur = (fournisseur) => {
    setEditingFournisseur(fournisseur);
    setFormData({
      nom: fournisseur.nom || '',
      type: fournisseur.type || 'Hôtel',
      pays: fournisseur.pays || '',
      ville: fournisseur.ville || '',
      adresse: fournisseur.adresse || '',
      telephone: fournisseur.telephone || '',
      email: fournisseur.email || '',
      personneContact: fournisseur.personneContact || '',
      telephoneContact: fournisseur.telephoneContact || '',
      emailContact: fournisseur.emailContact || '',
      statut: fournisseur.statut || 'Actif',
      statutPartenariat: fournisseur.statutPartenariat || 'Nouveau',
      description: fournisseur.description || ''
    });
    setOpenDialog(true);
  };

  // Function to format currency correctly
  const formatCurrencyForPDF = (amount) => {
    // Convert number to string with spaces as thousand separators
    const numberPart = Math.round(amount || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${numberPart} DZD`;
  };

  const exportFournisseursToPDF = () => {
    try {
      // Create new PDF document
      const doc = new jsPDF('landscape');
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      
      // Colors
      const primaryColor = [30, 64, 175]; // blue
      const secondaryColor = [124, 58, 237]; // purple
      const successColor = [16, 185, 129]; // green
      const warningColor = [245, 158, 11]; // amber
      const textColor = [31, 41, 55]; // dark gray
      
      // Add header
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      // Add agency logo and name
      if (settings.agencyLogo) {
        try {
          // Add the agency logo from base64 string
          doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
          
          // Add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
        } catch (logoError) {
          console.error('Error adding logo to PDF:', logoError);
          // Fallback to text only
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
      } else {
        // No logo, just add agency name
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
      }
      
      // Add title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text("Rapport des Fournisseurs", pageWidth - margin, 17, { align: 'right' });
      
      // Add date and filters
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(10);
      doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, margin, 35);
      doc.text(`Filtres: Type: ${filterType === 'all' ? 'Tous' : filterType} | Statut: ${filterStatus === 'all' ? 'Tous' : filterStatus} | Région: ${filterLocation === 'all' ? 'Toutes' : filterLocation}`, margin, 42);
      
      // Add summary section
      doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setLineWidth(0.5);
      doc.line(margin, 48, pageWidth - margin, 48);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Résumé des Fournisseurs", margin, 58);
      
      // Calculate some key metrics
      const totalFournisseurs = filteredFournisseurs.length;
      const activeFournisseurs = filteredFournisseurs.filter(f => f.statut === 'Actif').length;
      const activePercentage = totalFournisseurs > 0 ? Math.round((activeFournisseurs / totalFournisseurs) * 100) : 0;
      
      // Get types and their counts
      const typeCounts = {};
      filteredFournisseurs.forEach(f => {
        if (!typeCounts[f.type]) {
          typeCounts[f.type] = 0;
        }
        typeCounts[f.type]++;
      });
      
      // Get countries and their counts
      const countryCounts = {};
      filteredFournisseurs.forEach(f => {
        if (f.pays && !countryCounts[f.pays]) {
          countryCounts[f.pays] = 0;
        }
        if (f.pays) {
          countryCounts[f.pays]++;
        }
      });
      
      // Summary stats
      const stats = [
        { label: "Total des Fournisseurs", value: `${totalFournisseurs}` },
        { label: "Fournisseurs Actifs", value: `${activeFournisseurs} (${activePercentage}%)` },
        { label: "Chiffre d'Affaires", value: formatCurrencyForPDF(totalChiffreAffaires) },
        { label: "Performance Moyenne", value: `${performanceMoyenne.toFixed(1)}%` },
        { label: "Note Moyenne", value: `${noteMoyenne.toFixed(1)}/5` }
      ];
      
      // Add KPI table
      doc.autoTable({
        startY: 62,
        head: [['Indicateur', 'Valeur']],
        body: stats.map(item => [item.label, item.value]),
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add type breakdown
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Répartition par Type", margin, doc.lastAutoTable.finalY + 15);
      
      const typeStats = Object.entries(typeCounts)
        .map(([type, count]) => ({
          type,
          count,
          percentage: (count / totalFournisseurs * 100).toFixed(1)
        }))
        .sort((a, b) => b.count - a.count);
      
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Type de Fournisseur', 'Nombre', 'Pourcentage']],
        body: typeStats.map(item => [
          item.type, 
          item.count.toString(),
          `${item.percentage}%`
        ]),
        theme: 'grid',
        headStyles: { fillColor: secondaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add location breakdown
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Répartition par Pays", margin, doc.lastAutoTable.finalY + 15);
      
      const countryStats = Object.entries(countryCounts)
        .map(([country, count]) => ({
          country,
          count,
          percentage: (count / totalFournisseurs * 100).toFixed(1)
        }))
        .sort((a, b) => b.count - a.count);
      
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Pays', 'Nombre de Fournisseurs', 'Pourcentage']],
        body: countryStats.map(item => [
          item.country, 
          item.count.toString(),
          `${item.percentage}%`
        ]),
        theme: 'grid',
        headStyles: { fillColor: successColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add supplier details
      doc.addPage();
      
      // Add header on new page
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      // Add agency logo and name
      if (settings.agencyLogo) {
        try {
          // Add the agency logo from base64 string
          doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
          
          // Add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
        } catch (logoError) {
          console.error('Error adding logo to PDF:', logoError);
          // Fallback to text only
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
      } else {
        // No logo, just add agency name
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
      }
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.text("Liste des Fournisseurs", pageWidth - margin, 17, { align: 'right' });
      
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(12);
      doc.text("Détails de tous les fournisseurs", margin, 40);
      
      // Add suppliers table
      const suppliersTableData = filteredFournisseurs.map(f => [
        f.id,
        f.nom,
        f.type,
        `${f.ville}, ${f.pays}`,
        f.personneContact,
        f.email,
        f.telephone,
        f.statut,
        f.commission ? `${f.commission}%` : 'N/A'
      ]);
      
      doc.autoTable({
        startY: 50,
        head: [['ID', 'Nom', 'Type', 'Localisation', 'Contact', 'Email', 'Téléphone', 'Statut', 'Commission']],
        body: suppliersTableData,
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 40 },
          2: { cellWidth: 25 },
          3: { cellWidth: 35 },
          4: { cellWidth: 30 },
          5: { cellWidth: 40 },
          6: { cellWidth: 25 },
          7: { cellWidth: 20 },
          8: { cellWidth: 22 }
        },
        margin: { left: margin, right: margin },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        didDrawPage: function (data) {
          // Add header on each page
          doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.rect(0, 0, pageWidth, 25, 'F');
          
          doc.setFillColor(255, 255, 255);
          doc.roundedRect(margin, 10, 30, 10, 1, 1, 'F');
          doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          // Add agency logo and name
        if (settings.agencyLogo) {
          try {
            // Add the agency logo from base64 string
            doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
            
            // Add agency name
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
          } catch (logoError) {
            console.error('Error adding logo to PDF:', logoError);
            // Fallback to text only
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
          }
        } else {
          // No logo, just add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
          
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(16);
          doc.text("Liste des Fournisseurs", pageWidth - margin, 17, { align: 'right' });
          
          // Add footer
          const pageNumber = doc.internal.getNumberOfPages();
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(`Page ${pageNumber}`, margin, pageHeight - 10);
          doc.text(`${settings.agencyName || 'Agence de Voyage'} | Page ${pageNumber} sur ${doc.internal.getNumberOfPages()}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
          doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        }
      });
      
      // Save the PDF with agency name
      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = `${settings.agencyName || 'Agence'}_Rapport_Fournisseurs_${dateStr}.pdf`;
      doc.save(fileName);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Export PDF réussi',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'export PDF',
        severity: 'error'
      });
    }
  };

  // Count interconnected data
  const connectedPackages = interconnectedPackages.length;
  const connectedTransactions = interconnectedTransactions.length;
  const connectedCountries = uniqueCountries.length;
  const connectedReservations = interconnectedReservations.length;

  return (
    <Box sx={{ p: { xs: 0, md: 3 } }} className="space-y-6">
      {/* Header Section */}
      <Box sx={{ 
        mb: { xs: 2, md: 4 },
        p: { xs: 2, md: 0 }
      }} className="flex justify-between items-center">
        <div>
          <Typography variant="h3" sx={{ 
            fontWeight: 800, 
            color: 'white',
            mb: 1.5,
            fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
            letterSpacing: '-0.01em'
          }}>
          Gestion des Fournisseurs
        </Typography>
        <Typography variant="body1" sx={{ 
          color: 'rgba(255,255,255,0.6)',
          fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
        }}>
          Gérez vos fournisseurs et partenaires commerciaux
        </Typography>
        </div>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* View Mode Toggle */}
          <Box sx={{ display: 'flex', bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, p: 0.5 }}>
            <Button
              onClick={() => setViewMode('table')}
              sx={{
                minWidth: 40,
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                color: viewMode === 'table' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                bgcolor: viewMode === 'table' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                '&:hover': {
                  bgcolor: viewMode === 'table' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <TableViewIcon />
            </Button>
            <Button
              onClick={() => setViewMode('card')}
              sx={{
                minWidth: 40,
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                color: viewMode === 'card' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                bgcolor: viewMode === 'card' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                '&:hover': {
                  bgcolor: viewMode === 'card' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <CardViewIcon />
            </Button>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
              color: 'white',
              px: 3,
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(135deg, #1e3a8a 0%, #6d28d9 100%)'
              }
            }}
          >
            Nouveau Fournisseur
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ width: '100%', mt: 4 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
            Chargement des données fournisseurs interconnectées...
          </Typography>
        </Box>
      ) : (
        <>
      {/* Quick Actions Section */}
      {showQuickActions && (
      <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SpeedIcon sx={{ mr: 1.5, color: '#f59e0b', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
            Actions Rapides
          </Typography>
        </Box>
        
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full cursor-pointer hover:bg-white/15 transition-all duration-300"
              onClick={() => handleOpenDialog()}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-800">
                  <BusinessIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Nouveau Fournisseur</h3>
              <p className="text-white/60 text-sm">Ajouter un fournisseur au système</p>
            </motion.div>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full cursor-pointer hover:bg-white/15 transition-all duration-300"
              onClick={exportFournisseursToPDF}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-green-600 to-green-800">
                  <PictureAsPdfIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Exporter PDF</h3>
              <p className="text-white/60 text-sm">Générer un rapport détaillé</p>
            </motion.div>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full cursor-pointer hover:bg-white/15 transition-all duration-300"
              onClick={() => setViewMode('cards')}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-amber-500 to-amber-700">
                  <BarChart className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Statistiques</h3>
              <p className="text-white/60 text-sm">Analyser les performances</p>
            </motion.div>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full cursor-pointer hover:bg-white/15 transition-all duration-300"
              onClick={() => setViewMode('list')}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-purple-600 to-purple-800">
                  <ViewList className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Liste Détaillée</h3>
              <p className="text-white/60 text-sm">Voir tous les fournisseurs</p>
            </motion.div>
          </Grid>
        </Grid>
      </Box>)}

      {/* Key Metrics Section */}
      <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AssessmentIcon sx={{ mr: 1.5, color: '#10b981', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
            Métriques en Temps Réel
          </Typography>
        </Box>
        
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {statsCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                    <p className="text-2xl font-bold text-white mt-2">
                      {stat.isCurrency ? formatCurrency(stat.value) : stat.value}
                    </p>
                    {stat.subtitle && (
                      <p className="text-white/60 text-sm mt-1">{stat.subtitle}</p>
                    )}
                    {stat.trend && (
                      <span className={`inline-flex items-center px-2 py-0.5 mt-2 rounded-full text-xs font-medium ${
                        stat.trend.startsWith('+') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {stat.trend}
                      </span>
                    )}
                  </div>
                  <div className={`p-3 rounded-2xl bg-gradient-to-r ${
                    index === 0 ? 'from-blue-600 to-indigo-600' : 
                    index === 1 ? 'from-green-600 to-green-800' : 
                    index === 2 ? 'from-amber-500 to-amber-700' : 
                    'from-purple-600 to-purple-800'
                  }`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Charts Section (désactivée) */}
      {false && (
      <Grid 
        container 
        spacing={{ xs: 2, md: 3 }} 
        sx={{ 
          px: { xs: 2, md: 0 },
          mb: { xs: 3, md: 4 },
          // Better spacing for mobile
          '& > .MuiGrid-item': {
            pb: { xs: 2, md: 3 }
          }
        }}
      >
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%', 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' } }}>
                  Performance des Fournisseurs
                </Typography>
                <IconButton size="small">
                  <MoreVert fontSize="small" />
                </IconButton>
              </Box>
              <SupplierPerformanceChart data={performanceChartData} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%', 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' } }}>
                  Distribution par Type
                </Typography>
                <Chip 
                  label="Ce mois" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              </Box>
              <SupplierDistributionChart data={typeDistribution} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* Search and Filters */}
      <Box sx={{ mb: 3, mx: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SearchIcon sx={{ mr: 1.5, color: '#f59e0b', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
            Recherche de Fournisseurs
          </Typography>
        </Box>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Rechercher un fournisseur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#f59e0b' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.08)',
                    },
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'rgba(255,255,255,0.5)',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: 'white',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.5)',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  }}
                >
                  <MenuItem value="all">Tous</MenuItem>
                  {uniqueTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Statut</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.5)',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  }}
                >
                  <MenuItem value="all">Tous</MenuItem>
                  <MenuItem value="Actif">Actifs</MenuItem>
                  <MenuItem value="Suspendu">Suspendus</MenuItem>
                  <MenuItem value="Inactif">Inactifs</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Pays</InputLabel>
                <Select
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.5)',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  }}
                >
                  <MenuItem value="all">Tous</MenuItem>
                  {uniqueCountries.map(pays => (
                    <MenuItem key={pays} value={pays}>{pays}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} className="flex justify-end space-x-2">
              <Button
                variant="contained"
                startIcon={<FilterAltIcon />}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  },
                }}
              >
                Filtrer
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                  },
                }}
              >
                Rechercher
              </Button>
            </Grid>
          </Grid>
        </motion.div>
      </Box>

      {/* Section Header */}
      <Box sx={{ px: { xs: 2, md: 0 }, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Business sx={{ mr: 1.5, color: '#8b5cf6', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
            Liste des Fournisseurs
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={exportFournisseursToPDF}
              sx={{ 
                borderRadius: 2,
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Exporter PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
                color: 'white'
              }}
            >
              Nouveau Fournisseur
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Enhanced Suppliers Display */}
      {false ? (
        <Grid container spacing={3} sx={{ 
          px: { xs: 2, md: 0 },
          // Better spacing for mobile
          '& > .MuiGrid-item': {
            pb: { xs: 2, md: 3 }
          }
        }}>
          {filteredFournisseurs
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((fournisseur) => (
              <Grid item xs={12} md={6} lg={4} key={fournisseur.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                      cursor: 'pointer'
                    }
                  }}
                      onClick={() => handleOpenDetails(fournisseur)}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar 
                        src={fournisseur.avatar}
                        sx={{ width: 48, height: 48 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {fournisseur.nom}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getTypeIcon(fournisseur.type)}
                          <Typography variant="body2" color="text.secondary">
                            {fournisseur.type}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton 
                        onClick={(e) => handleMenuClick(e, fournisseur)}
                        size="small"
                      >
                        <MoreVertIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                      </IconButton>
                    </Box>

                    {/* Location and Contact */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LocationIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.7)' }} />
                        <Typography variant="body2" color="text.secondary">
                          {fournisseur.ville}, {fournisseur.pays}
                        </Typography>
                        <Chip
                          label={fournisseur.statut}
                          color={getStatusColor(fournisseur.statut)}
                          size="small"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.7)' }} />
                        <Typography variant="body2" color="text.secondary">
                          {fournisseur.personneContact}
                        </Typography>
                      </Box>
                    </Box>

                    {/* No performance metrics - only showing user-entered data */}

                    {/* Commission - user-entered data */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {fournisseur.commission}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Commission
                        </Typography>
                      </Box>
                    </Box>

                    {/* Interconnected Data */}
                    {fournisseur.forfaitsGeres?.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="success.main" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                          📦 Forfaits Gérés
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {fournisseur.forfaitsGeres.slice(0, 2).map((forfait, index) => (
                            <Chip
                              key={index}
                              label={forfait.nom}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          ))}
                          {fournisseur.forfaitsGeres.length > 2 && (
                            <Chip
                              label={`+${fournisseur.forfaitsGeres.length - 2}`}
                              size="small"
                              color="default"
                            />
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Financial Summary */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: 'rgba(237, 108, 2, 0.04)', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Paiement
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {fournisseur.delaiPaiement}j
                        </Typography>
                      </Box>
                    </Box>

                    {/* Add Modifier and Supprimer buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Button 
                        size="small" 
                        startIcon={<EditIcon />}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click
                          handleOpenDialog(fournisseur);
                        }}
                      >
                        Modifier
                      </Button>
                      <Button 
                        size="small" 
                        color="error" 
                        startIcon={<DeleteIcon />}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click
                          handleDeleteFournisseur(fournisseur.id);
                        }}
                      >
                        Supprimer
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      ) : (
        <>
          {/* Table View */}
          {viewMode === 'table' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden relative mx-2 md:mx-0">
            {isLoading && (
              <LinearProgress 
                sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  height: 3, 
                  borderTopLeftRadius: 3,
                  borderTopRightRadius: 3,
                  zIndex: 1
                }} 
              />
            )}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-4 text-left text-white font-semibold">Fournisseur</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Type</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Localisation</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Commission</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Délai Paiement</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Statut</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center p-12 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
                        <p className="text-white/60">Chargement des fournisseurs...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredFournisseurs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center p-12 text-center">
                        <Business className="h-16 w-16 text-white/30 mb-4" />
                        <h3 className="text-xl font-semibold text-white mb-2">Aucun fournisseur trouvé</h3>
                        <p className="text-white/60 mb-6">Aucun résultat ne correspond à vos critères de recherche.</p>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => setOpenDialog(true)}
                          sx={{ 
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #7c3aed, #5b21b6)'
                            },
                          }}
                        >
                          Ajouter un fournisseur
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredFournisseurs
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((fournisseur) => (
                    <motion.tr
                      key={fournisseur.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-white/5 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-xl bg-blue-500/20">
                            <Avatar 
                              sx={{ 
                                bgcolor: getTypeColor(fournisseur.type) + '.main',
                                width: 42,
                                height: 42
                              }}
                            >
                              {fournisseur.nom?.split(' ')?.map(n => n?.[0] || '').join('') || 'FN'}
                            </Avatar>
                          </div>
                          <div>
                            <p className="text-white font-medium flex items-center gap-1">
                              {fournisseur.nom}
                              {fournisseur.isPreferred && (
                                <StarIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                              )}
                            </p>
                            <p className="text-white/60 text-sm">
                              {fournisseur.personneContact || 'Contact non spécifié'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-white font-medium flex items-center gap-1">
                            {getTypeIcon(fournisseur.type)}
                            {fournisseur.type}
                          </p>
                          <p className="text-white/60 text-sm">
                            {fournisseur.email || 'Email non spécifié'}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-white font-medium">
                            {fournisseur.ville || 'Ville non spécifiée'}
                          </p>
                          <p className="text-white/60 text-sm flex items-center gap-1">
                            <LocationIcon sx={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.7)' }} />
                            {fournisseur.pays || 'Pays non spécifié'}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-green-400 font-semibold">
                          {fournisseur.commission || 0}%
                        </p>
                        <p className="text-white/60 text-sm">
                          Commission
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-white font-medium">
                          {fournisseur.delaiPaiement || 0} jours
                        </p>
                        <div className="flex items-center space-x-1 text-xs text-white/70">
                          <ScheduleIcon className="h-3 w-3 text-white/60" />
                          Délai de paiement
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${fournisseur.statut === 'Actif' ? 'bg-green-500/20 text-green-300 border border-green-500/30' : fournisseur.statut === 'Suspendu' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'}`}>
                          {fournisseur.statut || 'Non défini'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleOpenDetails(fournisseur)}
                            className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                            title="Voir les détails"
                          >
                            <VisibilityIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleOpenDialog(fournisseur)}
                            className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors"
                            title="Modifier"
                          >
                            <EditIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteFournisseur(fournisseur.id)}
                            className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                            title="Supprimer"
                          >
                            <DeleteIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 flex items-center justify-between border-t border-white/10">
              <div className="text-sm text-white/60">
                Affichage de {(page * rowsPerPage) + 1} à {Math.min((page + 1) * rowsPerPage, filteredFournisseurs.length)} sur {filteredFournisseurs.length} fournisseurs
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <button
                  onClick={() => setPage(Math.min(Math.ceil(filteredFournisseurs.length / rowsPerPage) - 1, page + 1))}
                  disabled={page >= Math.ceil(filteredFournisseurs.length / rowsPerPage) - 1}
                  className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
            </div>
              </div>
            </motion.div>
          )}

          {/* Card View */}
          {viewMode === 'card' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="px-2 md:px-0"
            >
              {filteredFournisseurs.length === 0 ? (
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-12 text-center">
                  <Business className="h-16 w-16 text-white/30 mb-4 mx-auto" />
                  <h3 className="text-xl font-semibold text-white mb-2">Aucun fournisseur trouvé</h3>
                  <p className="text-white/60 mb-6">Aucun résultat ne correspond à vos critères de recherche.</p>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                    sx={{ 
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #7c3aed, #5b21b6)'
                      },
                    }}
                  >
                    Ajouter un fournisseur
                  </Button>
                </div>
              ) : (
                <>
                  <Grid container spacing={3}>
                    {paginatedFournisseurs.map((fournisseur) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={fournisseur.id}>
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3 }}
                          className="h-full"
                        >
                          <Card
                            sx={{
                              height: '100%',
                              background: 'rgba(255, 255, 255, 0.1)',
                              backdropFilter: 'blur(20px)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: 3,
                              color: 'white',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              display: 'flex',
                              flexDirection: 'column',
                              '&:hover': {
                                background: 'rgba(255, 255, 255, 0.15)',
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                              },
                            }}
                            onClick={() => handleViewFournisseur(fournisseur)}
                          >
                            <CardContent sx={{ p: 3, pb: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                              {/* Header with Avatar and Type */}
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar 
                                  sx={{ 
                                    bgcolor: getTypeColor(fournisseur.type) + '.main',
                                    width: 48,
                                    height: 48,
                                    mr: 2
                                  }}
                                >
                                  {fournisseur.nom?.split(' ')?.map(n => n?.[0] || '').join('') || 'FN'}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="h6" sx={{ 
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    mb: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {fournisseur.nom}
                                    {fournisseur.isPreferred && (
                                      <StarIcon sx={{ fontSize: 16, color: '#f59e0b', ml: 1 }} />
                                    )}
                                  </Typography>
                                  <Chip
                                    label={fournisseur.type}
                                    size="small"
                                    sx={{
                                      backgroundColor: `rgba(${getTypeColor(fournisseur.type) === 'primary' ? '59, 130, 246' : 
                                        getTypeColor(fournisseur.type) === 'success' ? '34, 197, 94' : 
                                        getTypeColor(fournisseur.type) === 'info' ? '6, 182, 212' : 
                                        getTypeColor(fournisseur.type) === 'warning' ? '245, 158, 11' : 
                                        getTypeColor(fournisseur.type) === 'error' ? '239, 68, 68' : '107, 114, 128'}, 0.2)`,
                                      color: 'white',
                                      fontWeight: 500,
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                </Box>
                              </Box>

                              {/* Location */}
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <LocationIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)', mr: 1 }} />
                                <Typography variant="body2" sx={{ 
                                  color: 'rgba(255, 255, 255, 0.8)',
                                  fontSize: '0.875rem'
                                }}>
                                  {fournisseur.ville}, {fournisseur.pays}
                                </Typography>
                              </Box>

                              {/* Contact Info */}
                              {fournisseur.telephone && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <PhoneIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)', mr: 1 }} />
                                  <Typography variant="body2" sx={{ 
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontSize: '0.75rem'
                                  }}>
                                    {fournisseur.telephone}
                                  </Typography>
                                </Box>
                              )}

                              {/* Status */}
                              <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Chip
                                  label={fournisseur.statut}
                                  size="small"
                                  sx={{
                                    backgroundColor: `rgba(${fournisseur.statut === 'Actif' ? '34, 197, 94' : 
                                      fournisseur.statut === 'En pause' ? '245, 158, 11' : '239, 68, 68'}, 0.8)`,
                                    color: 'white',
                                    fontWeight: 500,
                                    fontSize: '0.75rem'
                                  }}
                                />
                                <Typography variant="body2" sx={{ 
                                  color: 'rgba(255, 255, 255, 0.6)',
                                  fontSize: '0.75rem'
                                }}>
                                  {fournisseur.delaiPaiement}j paiement
                                </Typography>
                              </Box>
                            </CardContent>

                            {/* Actions */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, pt: 0 }}>
                              <Tooltip title="Voir les détails">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewFournisseur(fournisseur);
                                  }}
                                  sx={{
                                    color: 'rgb(34, 197, 94)',
                                    bgcolor: 'rgba(34, 197, 94, 0.1)',
                                    '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.2)' },
                                    width: 36,
                                    height: 36,
                                  }}
                                >
                                  <VisibilityIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Modifier">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditFournisseur(fournisseur);
                                  }}
                                  sx={{
                                    color: 'rgb(245, 158, 11)',
                                    bgcolor: 'rgba(245, 158, 11, 0.1)',
                                    '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.2)' },
                                    width: 36,
                                    height: 36,
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Supprimer">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteFournisseur(fournisseur.id);
                                  }}
                                  sx={{
                                    color: 'rgb(239, 68, 68)',
                                    bgcolor: 'rgba(239, 68, 68, 0.1)',
                                    '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' },
                                    width: 36,
                                    height: 36,
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Pagination for card view */}
                  <Box sx={{ 
                    mt: 4, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: 3,
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      Affichage de {(page * rowsPerPage) + 1} à {Math.min((page + 1) * rowsPerPage, filteredFournisseurs.length)} sur {filteredFournisseurs.length} fournisseurs
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setPage(Math.max(0, page - 1))}
                        disabled={page === 0}
                        sx={{
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        Précédent
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setPage(Math.min(Math.ceil(filteredFournisseurs.length / rowsPerPage) - 1, page + 1))}
                        disabled={page >= Math.ceil(filteredFournisseurs.length / rowsPerPage) - 1}
                        sx={{
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        Suivant
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
            </motion.div>
          )}
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }
        }}
      >
        <MenuListItem onClick={() => { handleOpenDialog(selectedFournisseur); handleMenuClose(); }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Modifier</ListItemText>
        </MenuListItem>
            <MenuListItem onClick={() => { handleOpenDetails(selectedFournisseur); handleMenuClose(); }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Voir détails</ListItemText>
        </MenuListItem>
        <MenuListItem>
          <ListItemIcon>
            <AssessmentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rapport performance</ListItemText>
        </MenuListItem>
        <MenuListItem>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Imprimer fiche</ListItemText>
        </MenuListItem>
        <Divider />
        <MenuListItem onClick={() => { handleDeleteFournisseur(selectedFournisseur?.id); handleMenuClose(); }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Supprimer</ListItemText>
        </MenuListItem>
      </Menu>

      {/* Enhanced Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
            {editingFournisseur ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Les données seront synchronisées avec les forfaits et finances
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom du fournisseur"
                value={formData.nom}
                onChange={(e) => setFormData({...formData, nom: e.target.value})}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Type de service</InputLabel>
                <Select
                  value={formData.type}
                  label="Type de service"
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="Hôtel">Hôtel</MenuItem>
                  <MenuItem value="Compagnie aérienne">Compagnie aérienne</MenuItem>
                  <MenuItem value="Transport">Transport</MenuItem>
                  <MenuItem value="Restaurant">Restaurant</MenuItem>
                  <MenuItem value="Assurance">Assurance</MenuItem>
                  <MenuItem value="Tour opérateur">Tour opérateur</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pays"
                value={formData.pays}
                onChange={(e) => setFormData({...formData, pays: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PublicIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ville"
                value={formData.ville}
                onChange={(e) => setFormData({...formData, ville: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={formData.telephone}
                onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse complète"
                value={formData.adresse}
                onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Personne de contact"
                value={formData.personneContact}
                onChange={(e) => setFormData({...formData, personneContact: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Statut du partenariat</InputLabel>
                <Select
                  value={formData.statut}
                  label="Statut du partenariat"
                  onChange={(e) => setFormData({...formData, statut: e.target.value})}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="Actif">Actif</MenuItem>
                  <MenuItem value="Suspendu">Suspendu</MenuItem>
                  <MenuItem value="Inactif">Inactif</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Financial Terms */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                Conditions Financières
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Commission (%)"
                      type="number"
                      value={formData.commission}
                      onChange={(e) => setFormData({...formData, commission: parseFloat(e.target.value)})}
                      inputProps={{ min: 0, max: 100, step: 0.5 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end" sx={{ color: 'rgba(255, 255, 255, 0.7) !important' }}>%</InputAdornment>
                      }}
                      sx={{ 
                        '& .MuiOutlinedInput-root': { 
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: 'white'
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255, 255, 255, 0.7)'
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: 'rgba(255, 255, 255, 0.5)',
                          opacity: 1
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Délai de paiement (jours)"
                      type="number"
                      value={formData.delaiPaiement}
                      onChange={(e) => setFormData({...formData, delaiPaiement: parseInt(e.target.value)})}
                      inputProps={{ min: 0, max: 365 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScheduleIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                          </InputAdornment>
                        ),
                        endAdornment: <InputAdornment position="end" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>jours</InputAdornment>
                      }}
                      sx={{ 
                        '& .MuiOutlinedInput-root': { 
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: 'white'
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255, 255, 255, 0.7)'
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: 'rgba(255, 255, 255, 0.5)',
                          opacity: 1
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description des services"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Décrivez les services proposés par ce fournisseur..."
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>


          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2, color: 'white' }}>
            Annuler
          </Button>
          <Button 
            onClick={handleSaveFournisseur}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e3a8a 0%, #6d28d9 100%)'
              }
            }}
          >
            {editingFournisseur ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

          {/* Supplier Details Dialog */}
          <Dialog
            open={detailsOpen}
            onClose={handleCloseDetails}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }
            }}
          >
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                Détails du Fournisseur
              </Typography>
              <IconButton onClick={handleCloseDetails}>
                <CancelIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <SupplierDetails supplier={selectedFournisseur} />
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button 
                onClick={handleCloseDetails} 
                variant="outlined" 
                sx={{ 
                  borderRadius: 2,
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Fermer
              </Button>
              <Button 
                onClick={() => { 
                  handleOpenDialog(selectedFournisseur); 
                  handleCloseDetails(); 
                }}
                variant="contained" 
                startIcon={<EditIcon />}
                sx={{ 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
                  }
                }}
              >
                Modifier
              </Button>
            </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
        </>
      )}
    </Box>
  );
};

export default Fournisseurs; 